import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { IconComponent } from '@libs/components/icons';
import { FileInputModel, FileInputValueModel, getAllowedFileExtensions, getAllowedFileSizeBytes } from '@libs/form/models';
import { ErrorDirective, InputAttributesDirective, InputValidatorsDirective } from '../../../directives';
import { InputBaseComponent } from '../../../models';
import { InputProviderFactory } from '../ng-input-provider';
import { FileDropZoneComponent } from './file-drop-zone';
import { FilePickerActionsComponent } from './file-picker-actions';
import { FilePreviewListComponent } from './file-preview-list';
import { FileUploadValidatorService } from './file-upload-validator.service';

@Component({
  selector: 'ui-form-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ...InputProviderFactory.getProviders(FileComponent),
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    IconComponent,
    ErrorDirective,
    DecimalPipe,
    FileDropZoneComponent,
    FilePickerActionsComponent,
    FilePreviewListComponent,
    InputAttributesDirective,
    InputValidatorsDirective,
  ],
})
export class FileComponent extends InputBaseComponent implements OnInit {
  private readonly fileUploadValidator: FileUploadValidatorService = inject(FileUploadValidatorService);

  @Input() public data!: FileInputModel;

  public override valueAccessor!: never;

  public readonly isDisabled: WritableSignal<boolean> = signal(false);

  public readonly allowedMaxSizeMB: Signal<number> = computed(() => {
    return getAllowedFileSizeBytes(this.data) / (1024 * 1024);
  });

  public readonly allowedExtensionsList: Signal<string> = computed(() => {
    return getAllowedFileExtensions(this.data).join(', ').toUpperCase();
  });

  public readonly selectedFiles: WritableSignal<FileInputValueModel[]> = signal([]);

  public readonly maxCountReached: Signal<boolean> = computed(() => {
    // By default, if not configured, we accept minimum one file
    const maxCount: number = this.data.attributes?.maxCount ?? 1;
    const currentCount: number = this.selectedFiles().length;

    return currentCount >= maxCount;
  });

  public override ngOnInit(): void {
    super.ngOnInit();

    if (this.data.uploadCallback) {
      this.control.addAsyncValidators(
        this.fileUploadValidator.validateUploadFn(this.data.uploadCallback),
      );
    }
  }

  /* istanbul ignore next */
  public override writeValue(): void {
    /* empty */
  }

  public override registerOnChange(fn: (value: unknown) => unknown): void {
    this.onChangeCallback = fn;
  }

  public override registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public override setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  public onFilesSelected(fileList: FileList | File[]): void {
    if (this.isDisabled()) {
      return;
    }

    if (this.data.attributes?.multiple) {
      this.addSelectedFiles(Array.from(fileList));
    } else {
      this.setSelectedFiles([fileList[0]]);
    }
  }

  public removeFile(index: number): void {
    const files: FileInputValueModel[] = [...this.selectedFiles()];

    files.splice(index, 1);

    this.updateSelectedFiles(files);
  }

  private addSelectedFiles(files: File[]): void {
    const selectedFiles: FileInputValueModel[] = this.selectedFiles();
    const maxCount: number | undefined = this.data.attributes?.maxCount;

    const uniqueNewFiles: File[] = files.filter(
      file => !selectedFiles.some(existing => {
        return existing.file.name === file.name
          && existing.file.size === file.size
          && existing.file.lastModified === file.lastModified;
      }),
    );

    if (uniqueNewFiles.length) {
      const remaining: number = maxCount ? Math.max(0, maxCount - selectedFiles.length) : uniqueNewFiles.length;

      this.updateSelectedFiles([
        ...selectedFiles,
        ...uniqueNewFiles.slice(0, remaining).map(file => ({ file })),
      ]);
    }
  }

  private setSelectedFiles(files: File[]): void {
    this.updateSelectedFiles(
      files.map(file => ({ file })),
    );
  }

  private updateSelectedFiles(files: FileInputValueModel[]): void {
    this.selectedFiles.set(files);
    this.onTouchedCallback();
    this.onChangeCallback(files.length ? files : null);
  }
}
